<script setup lang="ts">
import {ref} from "vue";
import {ElNotification, genFileId, UploadInstance, UploadRawFile} from "element-plus";
import TestCateSelector from "components/test/TestCateSelector.vue";
import ReelCateSelector from "components/test/ReelCateSelector.vue";
import {TestService} from "@/api/api.ts";

const type = ref(1)
const testCateSelector = ref<InstanceType<typeof TestCateSelector>>()
const reelCateSelector = ref<InstanceType<typeof ReelCateSelector>>()
const upload = ref<UploadInstance>()
const excelFile = ref<File>();
const excelName = ref<string>("");
const reelName = ref<string>("");
const reelSuffix = ref<string>("");
const isShowing = ref(false)
const reelScore = ref<string>("0.0");
const emit = defineEmits(["onBatchAddSuccess"])

const importFile = (e: any) => {
  if (!e || !e.file || !e.file.name) {
    console.warn("上传文件名为空");
    return;
  }
  handleFileChange(e.file)
};

const handleFileChange = (file: File) => {
  const fileName = file.name;
  const lastDotIndex = fileName.lastIndexOf(".");
  reelName.value = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
  reelSuffix.value = fileName.slice(lastDotIndex);
  excelName.value = file.name;
  excelFile.value = file;
}

const onExceed = (files: any) => {
  upload.value!.clearFiles()
  const file = files[0] as UploadRawFile
  file.uid = genFileId()
  upload.value!.handleStart(file)
  handleFileChange(file)
}

const hide = () => {
  isShowing.value = false
  excelFile.value = undefined;
  excelName.value = "";
  reelName.value = ""
}

const show = (_type: number = 1) => {
  type.value = _type
  isShowing.value = true
}

const onSubmit = async () => {
  if (!excelFile.value) {
    return ElNotification.warning({
      message: "请选择文件",
    })
  }
  try {
    const formData = new FormData();
    const fCateId = testCateSelector.value?.getFCateId()
    const sCateId = testCateSelector.value?.getSCateId()
    const oneCateId = reelCateSelector.value?.getOneCateId()
    const twoCateId = reelCateSelector.value?.getTwoCateId()
    const threeCateId = reelCateSelector.value?.getThreeCateId()
    if (!fCateId || !sCateId) {
      return ElNotification.warning({
        message: "请选择题目分类",
      })
    }
    formData.append("f_cate_id", fCateId?.toString() || "")
    formData.append("s_cate_id", sCateId?.toString() || "")
    formData.append("file", excelFile.value as Blob);
    if (type.value == 2) {
      if (!reelName.value) {
        return ElNotification.warning({
          message: "请输入套卷名称",
        })
      }
      formData.append("reel_name", reelName.value)
      if (reelScore.value.length === 0) {
        return ElNotification.warning({
          message: "请输入套卷分数",
        })
      }
      formData.append("score", reelScore.value)
      formData.append("one_cate_id", oneCateId?.toString() || "")
      formData.append("two_cate_id", twoCateId?.toString() || "")
      formData.append("three_cate_id", threeCateId?.toString() || "")
    }
    const res = await TestService.addBatchQuestion(formData)
    if (res.data.code === 200) {
      ElNotification.success({
        message: res.data.msg,
      })
      hide()
      emit("onBatchAddSuccess")
    } else {
      ElNotification.error({
        message: res.data.msg,
      })
    }
  } catch (err) {
    console.warn(err)
  }
}

const onFCateChange = () => {
  reelCateSelector.value?.resetReelCateList()
}

const onSubCateChange = (cateId: number) => {
  reelCateSelector.value?.getReelCate(cateId)
}

defineExpose({
  show, hide
})
</script>

<template>
  <el-dialog destroy-on-close v-model="isShowing" title="批量添加题目" align-center>
    <el-form label-width="auto">
      <el-form-item label="上传类型">
        <el-radio-group v-model="type">
          <el-radio-button :value="1">普通上传</el-radio-button>
          <el-radio-button :value="2">上传套卷</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <test-cate-selector
          ref="testCateSelector"
          @on-f-cate-change="onFCateChange"
          @on-cate-change="onSubCateChange"/>
      <div v-show="type === 2">
        <reel-cate-selector ref="reelCateSelector"/>
        <el-form-item label="套卷名称" v-show="type === 2">
          <el-input class="my-input" v-model="reelName">
            <template #append>
              {{ reelSuffix }}
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="套卷分数" v-show="type === 2">
          <el-input v-model="reelScore" class="my-input">
            <template #append>
              分
            </template>
          </el-input>
        </el-form-item>
      </div>
      <el-upload
          ref="upload"
          :multiple="false"
          :show-file-list="true"
          :http-request="importFile"
          :on-exceed="onExceed"
          :limit="1"
          accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">
        <template #trigger>
          <el-button type="primary">选择文件</el-button>
        </template>
      </el-upload>
      <a href="https://view.bioquan.com/web/%E9%A2%98%E5%BA%93%E6%96%B0%E6%A0%B7%E5%BC%8F.xlsx">下载模板文件</a>
      <div class="text-red-4 text-18px mt-1rem">上传前请清空表格内样版内容</div>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="hide">取消</el-button>
        <el-button type="primary" @click="onSubmit">
          确认上传
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped>
</style>
